import { Flex, LinkButton, Span, Text } from "@hopper-ui/components";
import { InfoIcon } from "@hopper-ui/icons";
import { Trans } from "@sg-protect/federation-runtime/i18n";
import { type ShellTranslationKey, useShellTranslation } from "../../../i18n/i18next.ts";
import { useTrackButtonClicked } from "../../../tracking/useTrackEvent.ts";
import { useLegacyHomePaths } from "../../../useLegacyHomePaths.ts";
import { useSubscriptionBanners } from "../../../useSubscriptionBanners.ts";
import type { ExpirationStatus } from "./selectors.ts";

const VIEW_PLANS_TRIGGER = "View plans";

const BANNER_MESSAGE_KEYS = {
    expiring: "SubscriptionExpirationBanner.status.expiring",
    "expired-single": "SubscriptionExpirationBanner.status.expired-single",
    "expired-trial-single": "SubscriptionExpirationBanner.status.expired-trial-single",
    "expired-all": "SubscriptionExpirationBanner.status.expired-all",
    "expired-some": "SubscriptionExpirationBanner.status.expired-some"
} as const satisfies Record<Exclude<ExpirationStatus, "expiring-trial">, ShellTranslationKey>;

interface BannerMessageProps {
    status: ExpirationStatus;
    daysLeft: number;
}

function BannerMessage({ status, daysLeft }: BannerMessageProps) {
    const { t, i18n } = useShellTranslation();

    if (status !== "expiring-trial") {
        return t(BANNER_MESSAGE_KEYS[status]);
    }

    const strong = <Span fontWeight="body-sm-semibold" />;

    return daysLeft === 0 ? (
        <Trans i18n={i18n} ns="shell" i18nKey="SubscriptionExpirationBanner.trialToday" components={{ strong }} />
    ) : (
        <Trans
            i18n={i18n}
            ns="shell"
            i18nKey="SubscriptionExpirationBanner.trialDaysLeft"
            count={daysLeft}
            components={{ strong }}
        />
    );
}

export function SubscriptionExpirationBanner() {
    const { t } = useShellTranslation();
    const trackButtonClicked = useTrackButtonClicked();
    const { subscription } = useLegacyHomePaths();
    const { isExpirationBannerVisible, expiration, trialDaysLeft, channel } = useSubscriptionBanners();

    if (!isExpirationBannerVisible || expiration == null) {
        return null;
    }

    return (
        <Flex
            alignItems="center"
            justifyContent="space-between"
            gap="inline-sm"
            backgroundColor="upsell"
            color="upsell"
            padding="inset-squish-md"
            data-public
        >
            <Flex alignItems="center" gap="inline-sm">
                <InfoIcon size="sm" flexShrink={0} />
                <Text size="sm">
                    <BannerMessage status={expiration.status} daysLeft={trialDaysLeft} />
                </Text>
                {channel === "reseller" && <Text size="sm">{t("SubscriptionExpirationBanner.contactReseller")}</Text>}
            </Flex>
            {channel === "direct" && (
                <LinkButton
                    size="sm"
                    variant="secondary"
                    href={subscription}
                    onPress={() => trackButtonClicked(VIEW_PLANS_TRIGGER, "SubscriptionExpirationBanner")}
                >
                    {t("SubscriptionExpirationBanner.viewPlans")}
                </LinkButton>
            )}
        </Flex>
    );
}
